import React, { useState, useEffect, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Grid,
    IconButton,
    CircularProgress,
    Chip,
} from '@mui/material';
import { ArrowBack, Save, VerifiedUser, Work, HourglassEmpty, Email } from '@mui/icons-material';
import { useSnackbar } from 'src/view/components/SnackbarService';
import clientService from 'src/clientService';
import { AppSchemasAuthUserUpdate, HTTPValidationError, AppSchemasAuthUserResponse, UserRole, UserStatus, HttpResponse } from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';

const UpdateUserPage: React.FC = () => {
    const navigate = useNavigate();
    const { showSnackbar, snackbarType } = useSnackbar();
    const { state, dispatch } = useAppState();
    const currentUser = state.user; // Assuming user details are in state.user

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
    });
    const [errors, setErrors] = useState({
        firstName: '',
        lastName: '',
    });

    useEffect(() => {
        if (currentUser) {
            setFormData({
                firstName: currentUser.first_name || '',
                lastName: currentUser.last_name || '',
            });
        } else {
            // Optionally fetch user details if not in state, though typically they should be after login
            showSnackbar({ message: 'User details not found. Please re-login.', type: snackbarType.ERROR });
            startTransition(() => {
                navigate('/auth'); // Or home
            });
        }
    }, [currentUser]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name as keyof typeof errors]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = (): boolean => {
        let isValid = true;
        const newErrors = { firstName: '', lastName: '' };

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First Name is required.';
            isValid = false;
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last Name is required.';
            isValid = false;
        }
        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        const payload: AppSchemasAuthUserUpdate = {
            first_name: formData.firstName,
            last_name: formData.lastName,
        };

        clientService.api
            .updateUserApiV1AuthUserPut(payload)
            .then((response: HttpResponse<AppSchemasAuthUserResponse, HTTPValidationError>) => {
                showSnackbar({ message: 'Profile updated successfully!', type: snackbarType.SUCCESS });
                // Update user details in global state
                startTransition(() => { // Wrap dispatch
                    dispatch({ type: 'SET_USER_DETAILS', payload: response.data });
                });
                // Optionally navigate away or refresh data
            })
            .catch((error: HTTPValidationError | any) => {
                const errorMessage = error?.detail?.[0]?.msg || error?.message || 'Error updating profile.';
                showSnackbar({
                    message: `Error: ${errorMessage}`,
                    type: snackbarType.ERROR,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (!currentUser) {
        return <Container><CircularProgress sx={{ display: 'block', margin: 'auto', mt: 5 }} /></Container>; // Or a more descriptive loading/error state
    }

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton onClick={() => startTransition(() => navigate(-1))} color="primary" sx={{ mr: 1 }} aria-label="Back" disabled={isSubmitting}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h5" component="h1">Update Profile</Typography>
                </Box>

                <Box mb={3} p={2} border={1} borderColor="divider" borderRadius={1}>
                    <Typography variant="h6" gutterBottom>User Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Chip icon={<Email />} label={`Email: ${currentUser.email}`} variant="outlined" sx={{ mr: 1, mb: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Chip icon={<Work />} label={`Role: ${currentUser.role || UserRole.User}`} variant="outlined" sx={{ mr: 1, mb: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Chip icon={<HourglassEmpty />} label={`Status: ${currentUser.status || UserStatus.Active}`} variant="outlined" sx={{ mr: 1, mb: 1 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Chip
                                icon={<VerifiedUser />}
                                label={`Email Verified: ${currentUser.is_email_verified ? 'Yes' : 'No'}`}
                                color={currentUser.is_email_verified ? 'success' : 'error'}
                                variant="outlined"
                                sx={{ mr: 1, mb: 1 }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button onClick={() => startTransition(() => navigate(-1))} color="inherit" disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !formData.firstName || !formData.lastName} startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default UpdateUserPage;